/**
 * @file     LegalDisclaimer.tsx
 * @readme   ./README.md
 * @purpose  Legal disclaimer and source citation block shown on every lesson. Protects against liability for outdated information.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiLink from '@mui/material/Link';
import Collapse from '@mui/material/Collapse';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import Link from 'next/link';
import { useAppTheme } from '@/core/theme';
import type { ContentSource } from '@/content-system/academy/types';
import { IconChevronDown, IconChevronUp } from './icons/AcademyIcons';

interface LegalDisclaimerProps {
  sources?: ContentSource[];
  lastVerified?: string;
  compact?: boolean;
}

export function LegalDisclaimer({ sources, lastVerified, compact = false }: LegalDisclaimerProps) {
  const { theme } = useAppTheme();
  const dur = theme.motion.durations;
  const ease = theme.motion.easing;
  const [showSources, setShowSources] = useState(false);
  const hasSources = sources && sources.length > 0;

  return (
    <Box
      sx={{
        borderRadius: `${theme.layout.radius.lg}px`,
        border: `1px solid ${theme.semanticColors.borderSubtle}`,
        backgroundColor: alpha(theme.rawSemanticColors.surfaceCard, 0.5),
        p: compact ? 1.5 : 2,
        mt: compact ? 1 : 2,
      }}
    >
      <Typography
        sx={{
          fontSize: compact ? 11 : 12,
          color: theme.semanticColors.textMuted,
          lineHeight: 1.6,
        }}
      >
        <strong>Disclaimer:</strong> This content is for general educational purposes only and does not constitute legal, financial, or real estate advice. Laws, regulations, tax rules, and government programs in British Columbia change frequently. <MuiLink component={Link} href="/about-ray" sx={{ color: theme.semanticColors.interactivePrimary, textDecoration: 'none', fontWeight: 500, fontSize: 'inherit', '&:hover': { opacity: 0.8 } }}>Ray Rasouli</MuiLink> reviews and updates this information often, but it may not reflect the most current rules.{' '}
        {lastVerified && (
          <span>Content last verified: {lastVerified}. </span>
        )}
        Always verify current rules directly with the relevant government authority or consult a licensed professional (lawyer, accountant, or licensed REALTOR) before making real estate decisions. Ray Rasouli and Relta accept no liability for actions taken based on this educational content.
      </Typography>

      {hasSources && (
        <Box sx={{ mt: 2 }}>
          <Box
            onClick={() => setShowSources(!showSources)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 },
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 500,
                color: theme.semanticColors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Sources ({sources.length})
            </Typography>
            {showSources ? <IconChevronUp size={10} /> : <IconChevronDown size={10} />}
          </Box>

          <Collapse in={showSources} timeout={dur.normal}>
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2, listStyleType: 'none' }}>
              {sources.map((source, i) => (
                <Box component="li" key={i} sx={{ mb: 1 }}>
                  <MuiLink
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontSize: 11,
                      color: theme.semanticColors.interactivePrimary,
                      textDecoration: 'none',
                      transition: `color ${dur.fast}ms ${ease.standard}`,
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {source.title}
                  </MuiLink>
                  <Typography component="span" sx={{ fontSize: 10, color: theme.semanticColors.textMuted, ml: 0.5 }}>
                    (retrieved {source.retrievedDate})
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

/** Compact one-line disclaimer for the landing page and journey map */
export function LandingDisclaimer() {
  const { theme } = useAppTheme();

  return (
    <Typography
      sx={{
        fontSize: 11,
        color: theme.semanticColors.textMuted,
        textAlign: 'center',
        maxWidth: 600,
        mx: 'auto',
        mt: 4,
        lineHeight: 1.5,
      }}
    >
      This is consumer education, not real estate licensure training and not legal or financial advice. <MuiLink component={Link} href="/about-ray" sx={{ color: theme.semanticColors.interactivePrimary, textDecoration: 'none', fontWeight: 500, fontSize: 'inherit', '&:hover': { opacity: 0.8 } }}>Ray Rasouli</MuiLink> reviews and updates this information often, but it may not reflect the most current BC laws or regulations. Always verify with official government sources or a licensed professional before making decisions.
    </Typography>
  );
}
